import React, { useEffect, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useWorkspaceService } from "../hooks/services/useWorkspaceService";
import PageSectionDivider from "../elements/PageSectionDivider";
import IconFont from "../elements/IconFont";
import NoWorkspace from "./NoWorkspace";
import useUserProfile from "../hooks/profile/useUserProfile";
import { TriggerToast } from "../globals/Toaster";
import { appRoutes } from "../../routes/appRoutes";

interface PendingInviteProps {
  siteName: string;
  from: string;
  siteLink: string;
  className: string;
  elementKey: number;
}

interface WorkspaceArrayProps {
  id: string;
  updated_by?: string | null;
  invite_id?: string | null;
}

interface InvitesArrayProps {
  siteName: string;
  from: string;
  siteLink: string;
}

const PendingInvites: React.FC = (): React.ReactElement => {

  const workspaceService = useWorkspaceService()
  const { profile } = useUserProfile()
  const canCreateWorkspace = profile?.can_create_workspace !== false
  const navigate = useNavigate();

  const handleCreateWorkspaceClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!canCreateWorkspace) {
      event.preventDefault();
      TriggerToast("Access Denied: Join Waitlist", undefined, 'error');
      return;
    }
    navigate(appRoutes.workspace.create);
  };

  const handleAcceptInvite = (inviteId: string) => {
    // handle accept invite logic here
    workspaceService.verifyWorkspaceInvite(inviteId).then((response) => {
      console.log(response)
    })
  }

  const pendingInvites: InvitesArrayProps[] = useMemo(() => {
    return []
  }, [])

  const PendingInvite: React.FC<PendingInviteProps> = ({ siteName, from, siteLink, elementKey, className }) => {
    /* add tailwind classes here, add any custom classes on the element */
    const elementClass = "rounded-[6px] p-[16px] border border-solid border-border-default flex gap-[8px] " + className;
    return (
      <div className={elementClass} key={elementKey}>
        <div><IconFont name="layers-2" size={24} /></div>
        <div className="grow">
          <h3 className="text-sm">{siteName}</h3>
          <span className="text-body-muted text-xs">Invite by {from}</span>
        </div>
        <div className="h-full my-auto">
          <button onClick={() => handleAcceptInvite(siteLink)} className="btn-secondary btn-sm w-auto px-[16px]">Accept</button>
        </div>
      </div>
    )
  }

  useEffect(() => {
    const getWorkspaceName = async ({ id }: { id: string }): Promise<string> => {
      const workspaceData = await workspaceService.getWorkspace(id);
      return workspaceData.name;
    }

    const getPendingInvites = async (): Promise<void> => {
      const workspaceArray: Array<WorkspaceArrayProps> = [];
      const invitesArray: Array<InvitesArrayProps> = [];

      try {
        workspaceArray.forEach(async (workspace) => {
          const workspaceName = await getWorkspaceName({ id: workspace.id });
          invitesArray.push({
            siteName: workspaceName,
            from: workspace.updated_by || '',
            siteLink: workspace.invite_id || ''
          });
        });
      } catch (err) {
        console.error('Error fetching workspaces ' + err);
      }
    }

    getPendingInvites();
  }, []);

  return (
    <div className="w-full">
      {pendingInvites.length === 0 ? <><NoWorkspace canCreateWorkspace={canCreateWorkspace} /></> : <>
        <div className="mb-[32px] text-center w-full">
          <h2 className="type-heading-lg">Pending Workspace Invites</h2>
          <p className="mt-[8px] text-body-muted text-sm">Invites not accepted after 7 days gets cancelled automatically and would need to be reinitiated by workspace owner.</p>
        </div>
        <div className="w-full flex flex-col gap-[8px]">
          {pendingInvites.map((invite, index) => {
            const inviteClass = `ui-pending-invite ui-pending-invite-${index}`
            return (<PendingInvite key={index} siteName={invite.siteName} from={invite.from} siteLink={invite.siteLink} elementKey={index} className={inviteClass} />)
          })}
        </div>
        <div className="w-full seperator flex items-center gap-[16px] my-[32px]">
          <PageSectionDivider className="my-0 flex-1" />
          <span>OR</span>
          <PageSectionDivider className="my-0 flex-1" />
        </div>
        <div className="new-workspace flex flex-col p-[16px] items-center rounded-[6px] border border-solid border-border-default gap-[8px]">
          <div className="new-workspace__text text-center">
            <h3 className="font-bold text-md">New workspace?</h3>
            <p className="text-sm">Dive into data-driven decisions, refine strategies, and elevate performance.</p>
          </div>
          <div className="new-workspace__link">
            <NavLink
              className="btn-primary"
              to={appRoutes.workspace.create}
              onClick={handleCreateWorkspaceClick}
            >
              Create New Workspace
            </NavLink>
          </div>
        </div>
      </>
      }
    </div>
  )
}

export default PendingInvites;
